import { useCallback, useEffect, useState } from 'react';
import { limitToLast, onValue, orderByChild, query, ref, type Database } from 'firebase/database';
import { fetchMessages } from './api';
import type { ChatMessage } from './types';

interface Options {
  db: Database | null;
  conversationId: string | null;
  enabled: boolean;
  tail?: number;
}

interface RawRtdbMessage {
  senderId: string;
  senderRole: 'user' | 'super_admin';
  body: string;
  createdAt: number;
}

export function useConversationMessages({ db, conversationId, enabled, tail = 50 }: Options) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Bootstrap via REST so we always have a known starting set even before RTDB is ready.
  useEffect(() => {
    if (!enabled || !conversationId) return;
    let cancelled = false;
    fetchMessages(conversationId, undefined, tail)
      .then((items) => {
        if (cancelled) return;
        setMessages([...items].reverse());
        setHasMore(items.length === tail);
      })
      .catch((e) => !cancelled && setError(e));
    return () => {
      cancelled = true;
    };
  }, [enabled, conversationId, tail]);

  // Live tail via RTDB.
  useEffect(() => {
    if (!db || !conversationId || !enabled) return;
    const r = query(
      ref(db, `/conversations/${conversationId}/messages`),
      orderByChild('createdAt'),
      limitToLast(tail),
    );
    const off = onValue(
      r,
      (snap) => {
        const val = snap.val() as Record<string, RawRtdbMessage> | null;
        if (!val) return;
        const items: ChatMessage[] = Object.entries(val)
          .map(([id, raw]) => ({
            id,
            conversation_id: conversationId,
            sender_user_id: raw.senderId,
            sender_role: raw.senderRole,
            body: raw.body,
            created_at: new Date(raw.createdAt).toISOString(),
          }))
          .sort((a, b) => a.created_at.localeCompare(b.created_at));
        setMessages((prev) => mergeById(prev, items));
      },
      (e) => setError(e as Error),
    );
    return () => off();
  }, [db, conversationId, enabled, tail]);

  const loadOlder = useCallback(async () => {
    if (!conversationId || loadingOlder || !hasMore) return;
    setLoadingOlder(true);
    try {
      const oldest = messages[0]?.created_at;
      const older = await fetchMessages(conversationId, oldest, 50);
      if (older.length < 50) setHasMore(false);
      setMessages((prev) => mergeById([...older].reverse(), prev));
    } finally {
      setLoadingOlder(false);
    }
  }, [conversationId, loadingOlder, hasMore, messages]);

  return { messages, loadOlder, loadingOlder, hasMore, error };
}

function mergeById(a: ChatMessage[], b: ChatMessage[]): ChatMessage[] {
  const map = new Map<string, ChatMessage>();
  for (const m of a) map.set(m.id, m);
  for (const m of b) map.set(m.id, m);
  return Array.from(map.values()).sort((x, y) => x.created_at.localeCompare(y.created_at));
}
